import { Inject, Injectable } from '@angular/core';
import { HttpCachedResponse } from '@app/core/http/models/http-cached-response.model';
import { HttpCacheConfig } from '@app/core/http/models/http-cache-config.model';
import { HttpResponse } from '@angular/common/http';
import { from, map, Observable, of, tap } from 'rxjs';
import { HTTP_CACHE_CONFIG } from '@app/core/http/providers/http-cache.provider';
import { StorageKey } from '@app/core/storage/storage-key.enum';
import { LocalStorageService } from '@app/core/storage/local-storage.service';

@Injectable({
    providedIn: 'root',
})
export class HttpCacheService {
    private _cache = new Map<string, HttpCachedResponse>();

    private readonly _millisTimeToLive = this.config.millisTimeToLive;
    private readonly _cacheKey = StorageKey.httpCache;
    private _revived = false;

    constructor(
        @Inject(HTTP_CACHE_CONFIG) private config: HttpCacheConfig,
        private localStorageService: LocalStorageService,
    ) {
        this.initCache();
    }

    initCache(): void {
        if (!this._revived) {
            this._cache = this._reviveCache();
            this.cleanExpiredItems();
        } else {
            this._revived = true;
        }
    }

    cacheResponse<T>(url: string, response: HttpResponse<T>): Observable<HttpResponse<string> | HttpResponse<T>> {
        const fromBlob = response.body instanceof Blob;
        const toCache$ = fromBlob ? this._blobToText(response.body).pipe(map((stringBody) => new HttpResponse({ body: this._buildStringBody(stringBody) }))) : of(response);
        return from(toCache$).pipe(
            tap((res) => {
                this._cache.set(url, { response: res, date: new Date(), fromBlob });
                this._saveCacheInStorage();
            }),
        );
    }

    retrieveResponse<R>(url: string): HttpResponse<R> | HttpResponse<Blob> {
        if (this.eligibleForRetrieval(url)) {
            const result = this._cache.get(url);
            if (result?.response) {
                return result.fromBlob
                    ? new HttpResponse({
                          ...result.response,
                          body: new Blob([result.response.body as BlobPart], { type: 'text/plain' }),
                      })
                    : (result.response as HttpResponse<R>);
            }
        } else {
            this.removeResponse(url);
            return undefined;
        }
    }

    cleanExpiredItems(): void {
        Array.from(this._cache.keys()).forEach((k) => {
            if (this.cacheExpired(k)) {
                this.removeResponse(k);
            }
        });
    }

    removeResponse(url: string): void {
        this._cache.delete(url);
        this._saveCacheInStorage();
    }

    eligibleForRetrieval(url: string): boolean {
        return this._cache.has(url) && !this.cacheExpired(url);
    }

    cacheExpired(url: string): boolean {
        const cachedResponse = this._cache.get(url);
        if (cachedResponse) {
            const nowMillis = Date.now();
            const cachedResponseMillis = new Date(cachedResponse.date).getTime();
            const diff = nowMillis - cachedResponseMillis;
            return diff >= this._millisTimeToLive;
        } else {
            return false;
        }
    }

    private _reviveCache(): Map<string, HttpCachedResponse> {
        const revivedCache = this.localStorageService.getItem<[string, HttpCachedResponse][]>(this._cacheKey);
        return new Map<string, HttpCachedResponse>(revivedCache);
    }

    private _saveCacheInStorage(): void {
        this.localStorageService.saveItem(this._cacheKey, this._cache);
    }

    private _blobToText(blob: Blob): Observable<string> {
        return new Observable<string>((subscriber) => {
            if (!blob) {
                subscriber.next('');
                subscriber.complete();
            } else {
                const reader = new FileReader();
                reader.onload = (event) => {
                    subscriber.next(event.target.result as string);
                    subscriber.complete();
                };
                reader.readAsText(blob);
            }
        });
    }

    private _buildStringBody(original: string): string {
        const obj: object = JSON.parse(original) as object;
        return 'result' in obj ? JSON.stringify(obj['result']) : JSON.stringify(obj);
    }
}
